import * as React from 'react'
import { ListBlock } from '@basehub/data/replicache/index'
import { useDroppable } from '@dnd-kit/core'
import {
  listBlockDndControllers,
  DroppableZone,
  DropIndicator,
  useDragStore,
  DroppableZoneProps,
} from '../dnd'
import { useTableDropAssets } from '../dnd/use-table-drop-assets'
import isEqual from 'lodash.isequal'

export const DroppableRightZone: React.FC<DroppableZoneProps> = React.memo(
  ({ parentBlock, parentPath, index, id, style, onDropFilesCallback }) => {
    const intendedIndex = index + 1
    const setActiveZoneId = useDragStore((state) => state.setActiveZoneId)
    const onDrop = useTableDropAssets({
      listBlock: parentBlock,
      path: parentPath,
    })
    const zoneId = `list-entry-droppable-right/${id}`
    const droppable = useDroppable({
      id: zoneId,
      data: listBlockDndControllers.droppableDataSchema.parse({
        intendedIndex,
        type: listBlockDndControllers.dndType,
      }),
      disabled: !id,
    })

    const isValid = React.useMemo(() => {
      if (!droppable.active || !droppable.isOver || !droppable.over) {
        return false
      }
      const parsed = listBlockDndControllers.draggableDataSchema.safeParse(
        droppable.active.data.current
      )

      if (!parsed.success) return false

      const activeItemData = parsed.data

      return isValidDnd({
        intendedIndex,
        dragItemIndex: activeItemData.index,
        isDraggingTo: activeItemData.shouldDropTo,
        parentBlockId: parentBlock.id,
      })
    }, [
      droppable.active,
      droppable.isOver,
      droppable.over,
      intendedIndex,
      parentBlock.id,
    ])

    React.useEffect(() => {
      if (droppable.isOver) {
        setActiveZoneId(zoneId)
      }
    }, [droppable.isOver, setActiveZoneId, zoneId])
    const alignRightClassName =
      'pointer-events-none z-50 block absolute h-full w-0.5 rounded-full bg-dark-accent top-0 -right-[9px] z-50'

    return (
      <>
        <span
          ref={droppable.setNodeRef}
          style={style}
          className="h-[calc(100%+theme(spacing.4))] top-0 absolute w-[calc(50%+theme(spacing.2))] z-50 -right-2 pointer-events-none"
        />
        <DropIndicator
          style={style}
          isVisible={isValid}
          className={alignRightClassName}
        />
        <DroppableZone
          style={style}
          id={id}
          zoneId={zoneId}
          onDrop={onDrop}
          onDropFilesCallback={onDropFilesCallback}
          className="h-[calc(100%+theme(spacing.4))] top-0 absolute w-[calc(50%+theme(spacing.2))] z-50 -right-2"
          indicatorClassName={alignRightClassName}
        />
      </>
    )
  },
  (prevProps, nextProps) => {
    return (
      prevProps.index === nextProps.index &&
      prevProps.id === nextProps.id &&
      prevProps.parentPath === nextProps.parentPath &&
      isEqual(prevProps.style, nextProps.style) &&
      prevProps.onDropFilesCallback === nextProps.onDropFilesCallback
    )
  }
)

export const DroppableLeftZone: React.FC<DroppableZoneProps> = React.memo(
  ({ parentBlock, parentPath, index, id, style, onDropFilesCallback }) => {
    const setActiveZoneId = useDragStore((state) => state.setActiveZoneId)
    const onDrop = useTableDropAssets({
      listBlock: parentBlock,
      path: parentPath,
    })
    const zoneId = `list-entry-droppable-left/${id}`
    const droppable = useDroppable({
      id: zoneId,
      data: listBlockDndControllers.droppableDataSchema.parse({
        intendedIndex: index,
        type: listBlockDndControllers.dndType,
      }),
      disabled: !id,
    })

    const isValid = React.useMemo(() => {
      if (!droppable.active || !droppable.isOver || !droppable.over) {
        return false
      }
      const parsed = listBlockDndControllers.draggableDataSchema.safeParse(
        droppable.active.data.current
      )

      if (!parsed.success) return false

      const activeItemData = parsed.data

      return isValidDnd({
        intendedIndex: index,
        dragItemIndex: activeItemData.index,
        isDraggingTo: activeItemData.shouldDropTo,
        parentBlockId: parentBlock.id,
      })
    }, [
      droppable.active,
      droppable.isOver,
      droppable.over,
      index,
      parentBlock.id,
    ])

    React.useEffect(() => {
      if (droppable.isOver) {
        setActiveZoneId(zoneId)
      }
    }, [droppable.isOver, setActiveZoneId, zoneId])

    const alignBottomClassName =
      'pointer-events-none z-50 block absolute h-full w-0.5 rounded-full bg-dark-accent top-0 -left-[9px] z-50'

    return (
      <>
        <span
          ref={droppable.setNodeRef}
          style={style}
          className="h-[calc(100%+theme(spacing.4))] top-0 absolute w-[calc(50%+theme(spacing.2))] z-50 -left-2 pointer-events-none"
        />
        <DropIndicator
          style={style}
          isVisible={isValid}
          className={alignBottomClassName}
        />
        <DroppableZone
          style={style}
          id={id}
          zoneId={zoneId}
          onDrop={onDrop}
          onDropFilesCallback={onDropFilesCallback}
          indicatorClassName={alignBottomClassName}
          className="h-[calc(100%+theme(spacing.4))] top-0 absolute w-[calc(50%+theme(spacing.2))] z-50 -left-2"
        />
      </>
    )
  }
)

export function isValidDnd({
  intendedIndex,
  dragItemIndex,
  isDraggingTo,
  parentBlockId,
}: {
  intendedIndex: number
  dragItemIndex: number
  isDraggingTo: ListBlock['id']
  parentBlockId: ListBlock['id']
}) {
  if (parentBlockId !== isDraggingTo) return false

  const isDraggingToSamePosition =
    // same index
    intendedIndex === dragItemIndex ||
    // also same index, cause this will be re-adjusted afterwards
    intendedIndex === dragItemIndex + 1

  if (isDraggingToSamePosition) return false

  return true
}
